import {
  LineOfCreditInstallmentState,
  Maybe,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import TransactionsSectionList from 'shared/components/TransactionSectionList/TransactionSectionList';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import {
  amountStyles,
  TransactionListItemProps,
} from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import theme from 'shared/styles/theme';

import {
  getPaymentSchedule,
  removePaymentSchedule,
} from './paymentScheduleRouteStore';

const Divider = () => {
  return (
    <View style={styles.dividerStyle}>
      <HairLineDivider />
    </View>
  );
};

interface PaymentScheduleHeaderProps {
  paidToDate: string;
  totalAmount?: Maybe<string | undefined>;
}

const PaymentScheduleHeader = ({
  paidToDate,
  totalAmount,
}: PaymentScheduleHeaderProps) => {
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'paymentSchedule',
  });

  return (
    <>
      <ListItem
        primaryCopy={paidToDate}
        lineFormat={ListItemLineFormat.LABEL}
        secondaryCopy={translatedText('paidToDate')}
        showsChevron={false}
      />
      <Divider />
      <ListItem
        primaryCopy={totalAmount ?? ''}
        lineFormat={ListItemLineFormat.LABEL}
        secondaryCopy={translatedText('remainingBalance')}
        showsChevron={false}
      />

      <View style={styles.space} />
    </>
  );
};

const PaymentScheduleScreen = () => {
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'paymentSchedule',
  });
  const {
    paymentScheduleId,
    totalAmount,
    paidToDate,
    subcategory = '',
  } = useLocalSearchParams<{
    paymentScheduleId?: string;
    paidToDate: string;
    totalAmount: string;
    subcategory?: string;
  }>();

  const { bottom } = useSafeAreaInsets();

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-line-of-credit/vloc-payment-schedule-custom',
    customAttributes: {
      subcategory,
    },
  });

  const [paymentSchedule] = useState(() =>
    getPaymentSchedule(paymentScheduleId),
  );

  useEffect(() => {
    return () => {
      removePaymentSchedule(paymentScheduleId);
    };
  }, [paymentScheduleId]);

  if (!paymentSchedule) {
    return (
      <LineOfCreditErrorScreenComponent
        screenAnalyticsId="/varo-line-of-credit/LineOfCreditErrorScreenComponent-paymentSchedule"
        trackingAnalyticsId="vlocError"
        trackingCustomAttributes={{
          step_name: VlocStepName.VLOC_GENERAL,
        }}
      />
    );
  }

  const transactionMap: TransactionListItemProps[] = paymentSchedule.map(
    (item) => {
      const amountText =
        typeof item.amount === 'string'
          ? item.amount
          : (item.amount?.formattedWithPlusPrefix ?? '');

      return {
        isPending:
          !item.installmentState ||
          item.installmentState === LineOfCreditInstallmentState.Paid ||
          item.installmentState ===
            LineOfCreditInstallmentState.UndefinedInstallmentState,
        titleText: item.title ?? '',
        analyticsId: item.id ?? '',
        amountText,
        amountStyle: amountStyles.NEUTRAL,
        tags: item.installmentState
          ? [
              {
                text: translatedText(item.installmentState ?? ''),
                type:
                  item.installmentState ===
                  LineOfCreditInstallmentState.Delinquent
                    ? 5
                    : 0,
              },
            ]
          : [],
      };
    },
  );

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('header'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <TransactionsSectionList
        transactions={transactionMap || []}
        header={{
          id: 'header',
          render: () => (
            <PaymentScheduleHeader
              paidToDate={paidToDate}
              totalAmount={totalAmount}
            />
          ),
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  dividerStyle: {
    paddingHorizontal: theme.spacing.m,
  },
  space: {
    backgroundColor: theme.color.background.tertiary,
    height: theme.spacing.s,
  },
});

export default PaymentScheduleScreen;
